import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { actions, selectors } from '@twaice-fe/frontend/shared/store';
import { SelectorTimeRange, TIME_RESOLUTION_MAPPING, TimeRangeInterface, TimeResolution } from '@twaice-fe/shared/models';
import { areDatesEqual, TimeLength, TimeRangeEnum, TimeRangeEnumDE } from '@twaice-fe/shared/utilities';
import { endOfMonth, startOfMonth } from 'date-fns';
import { combineLatest, filter, Observable, Subject, take, takeUntil } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { TimezoneDateHelperService } from './timezone-date-helper.service';
import { tzEndOfDay } from '@twaice-fe/frontend/shared/utilities';

const { systemSelectors } = selectors;
const { systemActions } = actions;

@Component({
  selector: 'twaice-fe-time-selector',
  templateUrl: './time-selector.component.html',
  styleUrl: './time-selector.component.scss',
  standalone: false,
})
export class TimeSelectorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() showFullYearSelector = false;
  @Input() isDataExplorerSolution = false;
  @Input() isWarrantySolution = false;

  timeRangeMapping: TimeRangeInterface[] = [];
  selectedTimeRange: TimeRangeInterface | null;
  selectedTimeRangeIndex: number;
  timeResolutionMapping: TimeRangeInterface[] = [
    { id: 'dailyView', value: 'date', label: 'Daily view', disabled: false },
    { id: 'monthlyView', value: 'month', label: 'Monthly view', disabled: false },
  ];
  selectedTimeResolution: TimeRangeInterface | null = this.timeResolutionMapping[0];
  pickerValue: [Date, Date];
  systemID: string;
  timeResolutionDE = Object.values(TIME_RESOLUTION_MAPPING);
  selectedTimeResolutionDE: TimeResolution | null =
    TIME_RESOLUTION_MAPPING.find((resolution) => resolution.value === TimeLength.QUARTER_HOUR) ?? null;
  date$: Observable<Date | null>;

  viewTimeRanges: SelectorTimeRange[];
  public ranges: NonNullable<unknown>;
  private destroy$ = new Subject<void>();

  constructor(
    protected store: Store,
    private tzDateHelper: TimezoneDateHelperService
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.store.select(systemSelectors.getSelectedTimeRange),
      this.tzDateHelper.resolvedTimezone$,
      this.tzDateHelper.storageTimezone$,
    ])
      .pipe(
        map(
          ([selectedTimeRange, resolvedTz, storageTz]) => ({
            selectedTimeRange,
            timezone: this.isDataExplorerSolution ? resolvedTz : storageTz,
          }),
          filter(({ selectedTimeRange: { timeRange } }) => !!timeRange)
        ),
        takeUntil(this.destroy$),
        distinctUntilChanged((lhs, rhs) => JSON.stringify(lhs) === JSON.stringify(rhs))
      )
      .subscribe(({ selectedTimeRange: { timeRange, availableTimeRanges, timeResolution }, timezone }) => {
        this.selectedTimeRange = timeRange ?? null;
        // we mutate this object so it needs to be unfrozen from the store
        if (!availableTimeRanges) {
          // available time ranges not yet set
          return;
        }

        const [startValue, storedEndValue] = this.selectedTimeRange?.value as [Date, Date];
        const endValue =
          this.isDataExplorerSolution && this.isLiveEndDate(this.selectedTimeRange?.id) ? new Date() : storedEndValue;

        this.timeRangeMapping = JSON.parse(JSON.stringify(availableTimeRanges));
        this.timeRangeMapping = this.timeRangeMapping.map((range) => ({
          ...range,
          value: (range.value as [Date, Date]).map((t) => new Date(t)) as [Date, Date],
        }));

        this.viewTimeRanges = this.timeRangeMapping.map((t, i) => ({ ...t, value: i }));

        if (this.isDataExplorerSolution) {
          this.selectedTimeResolution = { id: 'dailyView', value: 'date', label: 'Daily view', disabled: false };
        }

        this.ranges = this.getTimeRanges(timezone, this.timeRangeMapping);
        this.viewTimeRanges = this.timeRangeMapping.map((t, i) => ({ ...t, value: i }));
        this.pickerValue = [
          this.tzDateHelper.utcToTzDisplay(startValue, timezone),
          this.tzDateHelper.utcToTzDisplay(endValue, timezone),
        ];

        this.selectedTimeRangeIndex = availableTimeRanges.findIndex((range) => range.id === timeRange?.id);
        this.selectedTimeResolutionDE = timeResolution ?? null;
        this.evaluateAvailableOptions();
      });

    this.date$ = this.store.select(systemSelectors.getDateValue).pipe(map((dateValue) => dateValue ?? null));
  }

  compareTimeResolutionDEFn(o1: { value: number } | undefined, o2: { value: number } | undefined): boolean {
    return o1?.value === o2?.value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showFullYearSelector) {
      this.evaluateAvailableOptions();
    }
  }

  evaluateAvailableOptions(): void {
    const fullYearObj = this.timeRangeMapping.find((t) => t.id === 'threeHundredSixtyFiveDays');
    if (fullYearObj) fullYearObj.disabled = !this.showFullYearSelector;
  }

  async onRangePickerChange(result: Date[]) {
    if (result[0] == null || result[1] == null) {
      return;
    }
    const preselectedRange =
      Object.entries(this.ranges).find(
        ([, range]) => range[0].getTime() === result[0].getTime() && range[1].getTime() === result[1].getTime()
      ) || null;

    this.pickerValue = [result[0], result[1]];

    if (this.selectedTimeResolution?.id === 'monthlyView' && areDatesEqual(result[0], result[1])) {
      const start = startOfMonth(result[0]);
      const end = endOfMonth(result[1]);
      this.pickerValue = [start, end];
    }

    const matchedTimeRange = preselectedRange ? this.timeRangeMapping.find((range) => range.label === preselectedRange[0]) : null;

    const tz$ = this.isDataExplorerSolution ? this.tzDateHelper.resolvedTimezone$ : this.tzDateHelper.storageTimezone$;

    tz$.pipe(take(1)).subscribe((storageTimezone) => {
      let timeRangeValue: [Date, Date];

      if (this.isDataExplorerSolution) {
        timeRangeValue = [
          this.tzDateHelper.tzDisplayToUtc(result[0], storageTimezone),
          this.tzDateHelper.tzDisplayToUtc(result[1], storageTimezone),
        ];
      } else if (matchedTimeRange) {
        const [start, storedEnd] = matchedTimeRange.value as [Date, Date];
        timeRangeValue = [new Date(start), storedEnd];
      } else {
        timeRangeValue = this.tzDateHelper.buildCustomTimeRangeValue(result[0], result[1], storageTimezone);
      }

      this.selectedTimeRange = {
        id: matchedTimeRange?.id ?? 'custom',
        value: timeRangeValue,
        label: preselectedRange ? preselectedRange[0] : 'Custom',
        disabled: false,
      };
      this.store.dispatch(
        systemActions.setTimeRange({
          timeRange: {
            ...this.selectedTimeRange,
          },
          isUserSelectedTimeRange: true,
        })
      );
    });
  }

  async onTimeResolutionChange(selectedTimeResolution: string) {
    this.selectedTimeResolution = this.timeResolutionMapping.find((t) => t.value === selectedTimeResolution) ?? null;
  }

  async onTimeResolutionDEChange(selectedTimeResolutionDE: TimeResolution) {
    this.store.dispatch(
      systemActions.setTimeResolution({
        granularity: selectedTimeResolutionDE,
      })
    );
  }

  onDatePickerChange(date: Date): void {
    this.store.dispatch(systemActions.setDateValue({ date }));
  }

  private isLiveEndDate(rangeId: string | undefined): boolean {
    return !['custom', 'yesterday', 'previousMonth'].includes(rangeId as string);
  }

  /**
   * Cap a UTC instant to end-of-today in the storage timezone.
   * Prevents displaying a future date when the user's timezone is ahead of the storage timezone.
   */
  private capToStorageTzToday(date: Date, storageTimezone: string | null): Date {
    if (!storageTimezone) return date;
    const endOfToday = tzEndOfDay(new Date(), storageTimezone);
    return date.getTime() > endOfToday.getTime() ? endOfToday : date;
  }

  private getTimeRanges(storageTimezone: string | null, timeRangeMapping: TimeRangeInterface[]) {
    return Object.entries(timeRangeMapping).reduce<Record<string, [Date, Date]>>((acc, [, possibleTimeRange]) => {
      const dateRange = possibleTimeRange.value as [Date, Date];
      const endDate = this.isDataExplorerSolution && this.isLiveEndDate(possibleTimeRange.id) ? new Date() : dateRange[1];

      return {
        ...acc,
        [possibleTimeRange.label]: [
          this.tzDateHelper.utcToTzDisplay(dateRange[0], storageTimezone),
          this.tzDateHelper.utcToTzDisplay(this.capToStorageTzToday(endDate, storageTimezone), storageTimezone),
        ],
      };
    }, {});
  }
}
